import { action, makeObservable, observable } from 'mobx';
import { INotificationsStore } from './NotificationsStore';
import { IUserDiagnosticsService } from 'services/UserDiagnosticsService';
import { getErrorMessage, storeResponseHandler } from 'utilities/ErrorHelper';
import { BaseStoreClass, IBaseStoreClass } from './BaseStoreClass';


import { IUsersService } from 'services/UsersService';
import { downloadFile } from 'utilities/SystemUtils';
import { t } from 'i18next';
import { ICreateTicketRequest, IUpdateDevopsLinkRequest, IUpdateStateRequest, IUserDiagnostic, IUserDiagnosticResponse, IUserDiagnosticsDownloadFileRequestDto, IUserDiagnosticsFileEntry, IUserDiagnosticsFileEntryReponse, IUserDiagnosticsSelectedFile, UserDiagnosticsState } from 'types/models/UserDiagnosticsModel';

export interface IUserDiagnosticsStore extends IBaseStoreClass {
  userDiagnostics: IUserDiagnostic[];
  selectedUserDiagnostic: IUserDiagnostic | null;
  selectedFileEntry: IUserDiagnosticsSelectedFile | null;
  imageCache: Map<string, Blob>; //FileName - Blob

  isLoadingUserDiagnostics: boolean;
  isDownloadingUserDiagnosticsFile: boolean;
  isCreatingTicket: boolean;
  isUpdatingState: boolean;

  isBusy: boolean;

  currentTicketResponseUrl: string | null;
  hasTicketResponse: boolean;

  setSelectedUserDiagnostic(userDiagnostic: IUserDiagnostic | null): void;
  getUserDiagnostics(): void;
  downloadUserDiagnosticsFile(fileId: string, request?: IUserDiagnosticsDownloadFileRequestDto);
  createTicket: (request: ICreateTicketRequest) => Promise<void>;
  updateUserDiagnosticState: (request: IUpdateStateRequest) => Promise<void>;
  resetTicketResponse: () => void;
}

export class UserDiagnosticsStore extends BaseStoreClass implements IUserDiagnosticsStore {
  userDiagnostics: IUserDiagnostic[] = [];
  selectedUserDiagnostic: IUserDiagnostic | null = null;
  selectedFileEntry: IUserDiagnosticsSelectedFile | null;
  imageCache: Map<string, Blob> = new Map<string, Blob>();

  isLoadingUserDiagnostics: boolean = false;
  isDownloadingUserDiagnosticsFile: boolean = false;
  isCreatingTicket: boolean = false;
  isUpdatingState: boolean = false;

  isBusy: boolean = this.isLoadingUserDiagnostics || this.isDownloadingUserDiagnosticsFile || this.isCreatingTicket || this.isUpdatingState;

  currentTicketResponseUrl: string | null;
  hasTicketResponse: boolean = false;

  _notificationsStore: INotificationsStore;
  _userDiagnosticsService: IUserDiagnosticsService;
  _usersService: IUsersService;

  constructor(
    userDiagnosticsService: IUserDiagnosticsService,
    usersService: IUsersService,
    notificationsStore: INotificationsStore) {
    super();

    this._notificationsStore = notificationsStore;
    this._userDiagnosticsService = userDiagnosticsService;
    this._usersService = usersService;

    makeObservable(this, {
      userDiagnostics: observable,
      isLoadingUserDiagnostics: observable,
      isDownloadingUserDiagnosticsFile: observable,
      isCreatingTicket: observable,
      isBusy: observable,
      currentTicketResponseUrl: observable,
      hasTicketResponse: observable,
      selectedUserDiagnostic: observable,
      selectedFileEntry: observable,
      imageCache: observable,
      setSelectedUserDiagnostic: action,
      getUserDiagnostics: action,
      downloadUserDiagnosticsFile: action,
    });
  }

  async getUserDiagnostics() {
    try {
      this.setIsLoadingUserDiagnostics(true);

      const userDiagnosticsResponse = await this._userDiagnosticsService.getAllUserDiagnostics({
        page: this.page - 1, //Backend Pagination starts from 0
        pageSize: this.take,
        includeDeleted: false
      });

      let userDiagnosticsDtoData: IUserDiagnosticResponse[] = userDiagnosticsResponse.data;
      if (!userDiagnosticsDtoData || userDiagnosticsResponse.isError) {
        return;
      }

      let userDiagnosticsData = this.convertAllDtos(userDiagnosticsDtoData);

      this.setUserDiagnostics(userDiagnosticsData);
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoadingUserDiagnostics(false);
    }
  }

  async downloadUserDiagnosticsFile(userDiagnosticsId: string | null, request?: IUserDiagnosticsDownloadFileRequestDto, shouldDownloadToBrowser: boolean = true) {
    if (userDiagnosticsId === null) {
      return
    }

    try {
      this.setIsDownloadingUserDiagnosticsFile(true);

      let cachedImageName: string | null = null;
      if (request?.specificFileUrl) {
        cachedImageName = `cachedImage_${request.specificFileUrl}`;
        const foundCacheItem = this.handleImageFileEntryCache(userDiagnosticsId, request?.specificFileUrl, cachedImageName, shouldDownloadToBrowser)
        if (foundCacheItem) {
          this.setIsDownloadingUserDiagnosticsFile(false);
          return;
        }
      }

      this._notificationsStore.addToast(
        t('UserDiagnostics.Toast.DownloadingLogs'),
        `${t('UserDiagnostics.Toast.StartedMsg')} ${userDiagnosticsId}`,
        'info'
      );

      const response = await this._userDiagnosticsService.getUserDiagnosticsFile(userDiagnosticsId, request);

      if (response && (response.status === 200 || response.status === 204)) {
        const blob = response.blob;
        if (request?.specificFileUrl && cachedImageName) {
          this.handleImageFileEntryDownload(userDiagnosticsId, blob, request?.specificFileUrl, cachedImageName, !shouldDownloadToBrowser);
        }
        else {
          this.setSelectedFileEntry(null)
        }

        if (shouldDownloadToBrowser) {
          downloadFile(blob, response.fileName);
        }

        let successMsg = `${t('Successfully downloaded User Diagnostics file for')} ${userDiagnosticsId}`;
        this._notificationsStore.addToast(
          t('UserDiagnostics.Toast.Success'),
          successMsg,
          'success'
        );
      } else {
        this._notificationsStore.addToast(
          t('UserDiagnostics.Toast.Error'),
          `${t('UserDiagnostics.Toast.ErrorMsg')} ${userDiagnosticsId}`,
          'error'
        );
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    }
    finally {
      this.setIsDownloadingUserDiagnosticsFile(false);
    }
  }

  public createTicket = async (request: ICreateTicketRequest) => {
    this.setIsCreatingTicket(true);
    let reponseResult: string | null = null;
    try {
      const response = await this._userDiagnosticsService.createTicket(request);
      storeResponseHandler(response, t('UserDiagnostics.Action.CreateTicket.Successful'), this._notificationsStore.addToast);

      if (response.data && !response.isError) {
        reponseResult = response.data;
        const updatedSuccessfully = this.triggerUpdateUserDiagnostic(request.userDiagnosticId);
        this.updateTicketResponse(true, reponseResult);
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsCreatingTicket(false);
    }
  };

  public toggleCloseUserDiagnosticState = async (userDiagnostic: IUserDiagnostic) => {
    let newState = UserDiagnosticsState.Closed;
    if (userDiagnostic.state === UserDiagnosticsState.Closed) {
      newState = this.transitionToReopenedState(userDiagnostic.azureDevopsLink)
    }
    this.updateUserDiagnosticState({ userDiagnosticId: userDiagnostic.id, newState: newState })
  }

  private transitionToReopenedState = (azureDevopsLink: string | null): UserDiagnosticsState => {
    if (!azureDevopsLink || azureDevopsLink.trim() === "") {
      return UserDiagnosticsState.New;
    }

    return UserDiagnosticsState.Tracked;
  }

  public updateUserDiagnosticState = async (request: IUpdateStateRequest) => {
    this.setIsUpdatingState(true);
    let reponseResult: boolean | null = null;
    try {
      const response = await this._userDiagnosticsService.updateUserDiagnosticsState(request);

      if (response.data && !response.isError) {
        reponseResult = response.data;
        if (reponseResult) {
          const updatedSuccessfully = this.triggerUpdateUserDiagnostic(request.userDiagnosticId);
        }
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    }
    finally {
      this.setIsUpdatingState(false);
    }
  }

  public updateDevopsLink = async (request: { userDiagnosticId: string; newLink: string | null }) => {
    this.setIsCreatingTicket(true);
    let reponseResult: boolean | null = null;
    try {
      const userDiagnostic = this.userDiagnostics.find(x => x.id === request.userDiagnosticId);
      if (!userDiagnostic) {
        return;
      }

      const response = await this._userDiagnosticsService.updateDevopsUrl(request as IUpdateDevopsLinkRequest);

      if (response.data && !response.isError) {
        reponseResult = response.data;
        if (reponseResult) {
          storeResponseHandler(response, t('UserDiagnostics.Action.UpdateDevopsLink.Successful'), this._notificationsStore.addToast);
          const updatedSuccessfully = this.triggerUpdateUserDiagnostic(request.userDiagnosticId);
        }
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsCreatingTicket(false);
    }
  };

  public resetTicketResponse = () => {
    this.updateTicketResponse(false, null);
  };

  private triggerUpdateUserDiagnostic = async (userDiagnosticsId: string): Promise<boolean> => {
    const updatedUserDiagnosticResponse = await this._userDiagnosticsService.getUserDiagnostic(userDiagnosticsId);
    if (updatedUserDiagnosticResponse.data && !updatedUserDiagnosticResponse.isError) {
      const updatedUserDiagnosticReponseResult = updatedUserDiagnosticResponse.data;
      let userDiagnosticsData = this.convertAllDtos([updatedUserDiagnosticReponseResult]);
      if (userDiagnosticsData.length > 0) {
        this.updateUserDiagnostic(userDiagnosticsData[0])
        return true;
      }
    }
    return false;
  }

  private handleImageFileEntryCache = (userDiagnosticsId: string, fileName: string, cachedImageName: string, shouldDownloadToBrowser: boolean): boolean => {
    const cachedBlob = this.imageCache.get(cachedImageName);
    if (cachedBlob) {
      if (shouldDownloadToBrowser) {
        downloadFile(cachedBlob, fileName);
      }
      else {
        this.setSelectedFileEntry({
          userDiagnosticsId: userDiagnosticsId,
          fileName: fileName,
          blob: cachedBlob,
        });
      }

      return true;
    }

    return false;
  }

  private handleImageFileEntryDownload = async (userDiagnosticsId: string, blob: Blob, fileName: string, cachedImageName: string, shouldOpenImage: boolean) => {
    if (!blob.type.startsWith('image/')) {
      return;
    }

    this.imageCache.set(cachedImageName, blob)
    if (shouldOpenImage) {
      this.setSelectedFileEntry({
        userDiagnosticsId: userDiagnosticsId,
        fileName: fileName,
        blob: blob,
      });
    }
    else {
      this.setSelectedFileEntry(null);
    }
  }

  // =========================== UTILS ===========================

  updateIsBusy() {
    this.isBusy = this.isLoadingUserDiagnostics || this.isDownloadingUserDiagnosticsFile || this.isCreatingTicket || this.isUpdatingState;
  }

  setIsLoadingUserDiagnostics(status: boolean) {
    this.isLoadingUserDiagnostics = status;
    this.updateIsBusy()
  }

  setIsDownloadingUserDiagnosticsFile(value: boolean) {
    this.isDownloadingUserDiagnosticsFile = value;
    this.updateIsBusy()
  }

  setIsCreatingTicket(value: boolean) {
    this.isCreatingTicket = value;
    this.updateIsBusy()
  }

  setIsUpdatingState(value: boolean) {
    this.isUpdatingState = value;
    this.updateIsBusy()
  }

  setUserDiagnostics(userDiagnostics: IUserDiagnostic[]) {
    this.userDiagnostics = userDiagnostics;
  }

  updateTicketResponse(status: boolean, ticketResponseUrl: string | null) {
    this.hasTicketResponse = status;
    this.currentTicketResponseUrl = ticketResponseUrl;
  }

  setSelectedUserDiagnostic = (userDiagnostic: IUserDiagnostic | null) => {
    this.selectedUserDiagnostic = userDiagnostic;
    this.resetTicketResponse();
  };

  setSelectedFileEntry = (selectedFileEntry: IUserDiagnosticsSelectedFile | null) => {
    this.selectedFileEntry = selectedFileEntry;
  };

  updateUserDiagnostic(userDiagnostic: IUserDiagnostic) {
    const index = this.userDiagnostics.findIndex((org) => org.id === userDiagnostic.id);

    if (index > -1) {
      this.userDiagnostics = [...this.userDiagnostics.slice(0, index), userDiagnostic, ...this.userDiagnostics.slice(index + 1)];
    }

    if (this.selectedUserDiagnostic?.id === userDiagnostic.id) {
      this.selectedUserDiagnostic = this.userDiagnostics[index];
    }

    // if it doesn't exist, add it
    if (index === -1) {
      this.userDiagnostics = [...this.userDiagnostics, userDiagnostic];
    }
  }

  convertAllDtos(dtos: IUserDiagnosticResponse[]): IUserDiagnostic[] {
    return dtos.map(dto => this.convertDtoToUserDiagnostic(dto));
  }

  convertDtoToUserDiagnostic(dto: IUserDiagnosticResponse): IUserDiagnostic {
    return {
      id: dto.id,
      operatorId: dto.operatorId,
      operatorEmail: dto.operatorEmail,
      organizationId: dto.organizationId,
      organizationName: dto.organizationName,
      appVersion: dto.appVersion,
      hasDiagnosticsPackage: !!dto.diagnosticsPackageBlobUrl,
      state: dto.state,
      azureDevopsLink: dto.azureDevopsLink,
      fileEntries: dto.fileEntries?.map(this.convertFileEntryDtos) || null,
      userDiagnosticsTags: dto.userDiagnosticsTags,
      userSubject: dto.userDiagnosticsContext.userSubject,
      userDescription: dto.userDiagnosticsContext.userDescription,
      rawInfoContext: dto.userDiagnosticsContext.rawInfoContext,
      addedAtUtc: dto.addedAtUtc,
    };
  }

  convertFileEntryDtos(dto: IUserDiagnosticsFileEntryReponse): IUserDiagnosticsFileEntry {
    return {
      fileType: dto.fileType,
      relativeFilePath: dto.relativeFilePath
    };
  }
}